'use client';

import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';

interface AuthGuardConfig {
    adminOnly?: boolean;
}

interface AuthGuardContextType {
    config: AuthGuardConfig;
    setConfig: (config: AuthGuardConfig) => void;
}

const AuthGuardContext = createContext<AuthGuardContextType | undefined>(undefined);

export function AuthGuardProvider({children, initialConfig = {}}: {
    children: ReactNode,
    initialConfig?: AuthGuardConfig
}) {
    const [config, setConfig] = useState<AuthGuardConfig>(initialConfig);

    return (
        <AuthGuardContext.Provider value={{config, setConfig}}>
            {children}
        </AuthGuardContext.Provider>
    );
}

export function useAuthGuard() {
    const context = useContext(AuthGuardContext);
    if (context === undefined) {
        throw new Error('useAuthGuard must be used within an AuthGuardProvider');
    }
    return context;
}

export function AuthGuardOverride({adminOnly, children}: { adminOnly: boolean, children: ReactNode }) {
    const {setConfig} = useAuthGuard();

    // We use a layout-friendly way to update config. 
    // Since we are in a component, we can use useEffect to update the parent's context.
    useEffect(() => {
        setConfig({adminOnly});
        console.log(`Set adminOnly to ${adminOnly} in AuthGuardOverride.`)
        // Optional: restore on unmount if needed, but usually layouts stay.
    }, [adminOnly, setConfig]);

    return <>{children}</>;
}
